import React, { useEffect, useMemo, useState } from "react";
import { eur } from "../../utils/formatovanie";
import { useExporterySplatkovyKalendar } from "../../utils/exportSplatkovy";

export default function SplatkovyKalendarKarta({
  skalRezim,
  splatkovyKalendar,
  splatkovyKalendarPS_skratenie,
  splatkovyKalendarPS_znizenie,
  investicnyPriebeh,
  ppOpen,
  ppClamped,
  ppRezim,
}) {

  const zCentovNaCeleEura = (c) => Math.round(c / 100);

  const aggrRocne = (mesiace = []) => {
    if (!mesiace.length) return [];
    const out = [];
    for (let i = 0; i < mesiace.length; i += 12) {
      const slice = mesiace.slice(i, i + 12);
      if (!slice.length) break;
      out.push({
        rok: Math.floor(i / 12) + 1,
        splatkaC: slice.reduce((s, r) => s + (r.splatkaC || 0), 0),
        istinaC: slice.reduce((s, r) => s + (r.istinaC || 0), 0),
        urokC: slice.reduce((s, r) => s + (r.urokC || 0), 0),
        zostatokC: slice[slice.length - 1].zostatokC,
      });
    }
    return out;
  };

  const investRocne = (graf = []) => {
    const out = [];
    for (let i = 11; i < graf.length; i += 12) {
      out.push({ rok: Math.floor(i / 12) + 1, hodnota: graf[i] });
    }
    if (graf.length && graf.length % 12 !== 0) {
      out.push({
        rok: Math.floor((graf.length - 1) / 12) + 1,
        hodnota: graf[graf.length - 1],
      });
    }
    return out;
  };

  const { exportujCSV, exportujPDF, refTabulka } = useExporterySplatkovyKalendar({
    skalRezim,
    splatkovyKalendar,
    aggrRocne,
    splatkovyKalendarPS_skratenie,
    splatkovyKalendarPS_znizenie,
    investicnyPriebeh,
    investRocne,
    zCentovNaCeleEura,
    ppOpen,
    ppClamped,
    ppRezim,
  });

  const [zobrazitVsetko, setZobrazitVsetko] = useState(false);
  const [aktualnaStrana, setAktualnaStrana] = useState(1);
  const [pocetRiadkovNaStranu, setPocetRiadkovNaStranu] = useState(() =>
    (typeof window !== "undefined" && window.innerWidth <= 640 ? 12 : 24)
  );

  useEffect(() => {
    const nastavPodlaSirky = () => {
      setPocetRiadkovNaStranu(window.innerWidth <= 640 ? 12 : 24);
    };

    nastavPodlaSirky();
    window.addEventListener("resize", nastavPodlaSirky);
    return () => window.removeEventListener("resize", nastavPodlaSirky);
  }, []);

  useEffect(() => {
    setAktualnaStrana(1);
  }, [skalRezim, zobrazitVsetko, pocetRiadkovNaStranu]);

  const dataTabulky = useMemo(() => {
    const baseM = splatkovyKalendar;
    const baseR = aggrRocne(splatkovyKalendar);

    const psS = splatkovyKalendarPS_skratenie;
    const psZ = splatkovyKalendarPS_znizenie;

    const invM = investicnyPriebeh?.graf || [];
    const invR = investRocne(invM);

    const payoffPS =
      ppRezim === "skratenie"
        ? (psS?.find((r) => (r.zostatokC ?? Infinity) <= 0)?.mesiac ?? null)
        : (psZ?.find((r) => (r.zostatokC ?? Infinity) <= 0)?.mesiac ?? null);

    const payoffInv = investicnyPriebeh?.payoffMonth ?? null;

    if (skalRezim === "mesiace") {
      return baseM.map((r, idx) => {
        const i = idx;
        const psRadokS = psS?.[i] || null;
        const psRadokZ = psZ?.[i] || null;
        const invVal = invM[i] ?? null;

        const jePSHit = payoffPS && (i + 1) === payoffPS;
        const jeInvHit = investicnyPriebeh?.payoffMonth && (i + 1) === investicnyPriebeh.payoffMonth;
        const lastInvestRow = i === invM.length - 1;

        return {
          key: i,
          prvyStlpec: i + 1,
          splatka: `${eur(zCentovNaCeleEura(r.splatkaC))} €`,
          istina: `${eur(zCentovNaCeleEura(r.istinaC))} €`,
          urok: `${eur(zCentovNaCeleEura(r.urokC))} €`,
          zostatok: `${eur(zCentovNaCeleEura(r.zostatokC))} €`,
          psSplatka:
            ppRezim === "znizenie"
              ? (psRadokZ ? `${eur(zCentovNaCeleEura(psRadokZ.splatkaC))} €` : "—")
              : null,
          psZostatok:
            (ppRezim === "skratenie" ? psRadokS : psRadokZ)
              ? `${eur(
                  zCentovNaCeleEura(
                    (ppRezim === "skratenie" ? psRadokS : psRadokZ).zostatokC
                  )
                )} €`
              : "—",
          investicia: invVal != null ? `${eur(Math.round(invVal))} €` : "—",
          jePSHit,
          jeInvHit,
          lastInvestRow,
        };
      });
    }

    return baseR.map((r, idx) => {
      const i0 = idx * 12;
      const i11 = i0 + 11;

      const psSslice = psS?.slice(i0, i11 + 1) || [];
      const psZslice = psZ?.slice(i0, i11 + 1) || [];

      const psSZostatok = psSslice.length ? psSslice[psSslice.length - 1].zostatokC : null;
      const psZZostatok = psZslice.length ? psZslice[psZslice.length - 1].zostatokC : null;

      const invRiadok = invR.find((x) => x.rok === idx + 1);

      const rok = idx + 1;
      const mesiacOd = idx * 12 + 1;
      const mesiacDo = Math.min((idx + 1) * 12, baseM.length);

      const jePSHit =
        Number.isFinite(Number(payoffPS)) &&
        Number(payoffPS) >= mesiacOd &&
        Number(payoffPS) <= mesiacDo;

      const jeInvHit =
        Number.isFinite(Number(payoffInv)) &&
        Number(payoffInv) >= mesiacOd &&
        Number(payoffInv) <= mesiacDo;

      const poslednyInvestRok =
        Array.isArray(invR) && invR.length
          ? invR[invR.length - 1]?.rok === rok
          : false;

      return {
        key: idx,
        prvyStlpec: idx + 1,
        splatka: `${eur(zCentovNaCeleEura(r.splatkaC))} €`,
        istina: `${eur(zCentovNaCeleEura(r.istinaC))} €`,
        urok: `${eur(zCentovNaCeleEura(r.urokC))} €`,
        zostatok: `${eur(zCentovNaCeleEura(r.zostatokC))} €`,
        psSplatka: ppRezim === "znizenie" ? "—" : null,
        psZostatok: (() => {
          const zc = ppRezim === "skratenie" ? psSZostatok : psZZostatok;
          return zc != null ? `${eur(zCentovNaCeleEura(zc))} €` : "—";
        })(),
        investicia: invRiadok ? `${eur(Math.round(invRiadok.hodnota))} €` : "—",
        jePSHit,
        jeInvHit,
        lastInvestRow: poslednyInvestRok,
      };
    });
  }, [
    skalRezim,
    splatkovyKalendar,
    splatkovyKalendarPS_skratenie,
    splatkovyKalendarPS_znizenie,
    investicnyPriebeh,
    ppRezim,
  ]);

  const jeMesiacovyRezim = skalRezim === "mesiace";
  const celkovyPocetRiadkov = dataTabulky.length;
  const celkovyPocetStran =
    jeMesiacovyRezim && !zobrazitVsetko
      ? Math.max(1, Math.ceil(celkovyPocetRiadkov / pocetRiadkovNaStranu))
      : 1;

  useEffect(() => {
    if (aktualnaStrana > celkovyPocetStran) {
      setAktualnaStrana(celkovyPocetStran);
    }
  }, [aktualnaStrana, celkovyPocetStran]);

  const zobrazeneRiadky = useMemo(() => {
    if (!jeMesiacovyRezim || zobrazitVsetko) return dataTabulky;

    const od = (aktualnaStrana - 1) * pocetRiadkovNaStranu;
    const po = od + pocetRiadkovNaStranu;
    return dataTabulky.slice(od, po);
  }, [dataTabulky, jeMesiacovyRezim, zobrazitVsetko, aktualnaStrana, pocetRiadkovNaStranu]);

  if (!skalRezim) return null;
  return (
    <div className="card-section card--full">
      <div className="prepay-head">
        <h2>Splátkový kalendár</h2>
      </div>

      <div className="schedule-wrap">
        <div className="schedule-actions">
          <span className="ltv-caption">Export:</span>
          <button type="button" className="ltv-btn" onClick={exportujCSV}>CSV</button>
          <button type="button" className="ltv-btn" onClick={exportujPDF}>PDF</button>
        </div>

        {jeMesiacovyRezim && (
          <div className="schedule-pagination">
            <div className="schedule-pagination__left">
              <label className="chk schedule-pagination__toggle">
                <input
                  type="checkbox"
                  checked={zobrazitVsetko}
                  onChange={(e) => setZobrazitVsetko(e.target.checked)}
                />
                Zobraziť všetko
              </label>
            </div>

            {!zobrazitVsetko && (
              <div className="schedule-pagination__right">
                <button
                  type="button"
                  className="ltv-btn"
                  onClick={() => setAktualnaStrana((s) => Math.max(1, s - 1))}
                  disabled={aktualnaStrana <= 1}
                >
                  Predchádzajúca
                </button>

                <span className="schedule-pagination__info">
                  Strana {aktualnaStrana} / {celkovyPocetStran}
                </span>

                <button
                  type="button"
                  className="ltv-btn"
                  onClick={() => setAktualnaStrana((s) => Math.min(celkovyPocetStran, s + 1))}
                  disabled={aktualnaStrana >= celkovyPocetStran}
                >
                  Ďalšia
                </button>
              </div>
            )}
          </div>
        )}

        <table className="schedule" ref={refTabulka}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", width: "90px" }}>
                {skalRezim === "mesiace" ? "Mesiac" : "Rok"}
              </th>

              <th>Splátka</th>
              <th>Istina</th>
              <th>Úrok</th>
              <th>Zostatok</th>

              {ppOpen && ppClamped?.events?.length > 0 && (
                <>
                  {ppRezim === "znizenie" && <th>S PS splátka</th>}
                  <th>S PS zostatok</th>
                </>
              )}

              {investicnyPriebeh && <th>Investícia</th>}
            </tr>
          </thead>

          <tbody>
            {zobrazeneRiadky.map((r) => (
              <tr
                key={r.key}
                className={`${r.jePSHit ? "hit-pp" : ""} ${r.jeInvHit ? "hit-inv" : ""}`}
              >
                <td style={{ textAlign: "left" }}>{r.prvyStlpec}</td>
                <td>{r.splatka}</td>
                <td>{r.istina}</td>
                <td>{r.urok}</td>
                <td>{r.zostatok}</td>

                {ppOpen && ppClamped?.events?.length > 0 && (
                  <>
                    {ppRezim === "znizenie" && (
                      <td className={r.jePSHit ? "cell-pp-hit" : ""}>
                        {r.psSplatka ?? "—"}
                      </td>
                    )}

                    <td className={`${r.jePSHit ? "cell-pp-hit cell-pp-outline" : ""}`}>
                      {r.psZostatok}
                    </td>
                  </>
                )}

                {investicnyPriebeh && (
                  <td
                    className={`${r.jeInvHit ? "cell-inv-hit cell-inv-outline" : ""} ${r.lastInvestRow ? "bold" : ""}`}
                  >
                    {r.investicia}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}